import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { io } from 'socket.io-client';

// Partner <-> admin chat. Messages are sent over REST and arrive live over the /partner-chat socket namespace.
// Default export: floating widget for the partner dashboard. Named export AdminPartnerChat: inbox for admins.

// Same rule as StaffChat: in production nginx serves /api and /socket.io on the site's own origin
const API_BASE = process.env.REACT_APP_API_URL ||
  (window.location.hostname === 'localhost' ? 'http://localhost:5000' : '');
const MAX_VOICE_SECONDS = 120;

// SQLite CURRENT_TIMESTAMP is UTC without a zone marker
const parseTs = (ts) => (ts ? new Date(String(ts).replace(' ', 'T') + (String(ts).includes('Z') ? '' : 'Z')) : null);
const fmtTime = (ts) => parseTs(ts)?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) || '';
const fmtDay = (ts) => {
  const d = parseTs(ts);
  if (!d) return '';
  const today = new Date();
  const yesterday = new Date(); yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};
const previewText = (msg) => (msg.message_type === 'voice' ? '🎤 Voice message' : msg.message_type === 'image' ? '📷 Photo' : msg.content || '');

// Two-note chime via Web Audio (no sound file needed)
export const playChatChime = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    [[880, 0], [1320, 0.14]].forEach(([freq, at]) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, ctx.currentTime + at);
      gain.gain.exponentialRampToValueAtTime(0.3, ctx.currentTime + at + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + at + 0.35);
      osc.start(ctx.currentTime + at);
      osc.stop(ctx.currentTime + at + 0.4);
    });
  } catch { /* audio blocked until the user interacts with the page */ }
};

const showBrowserNotification = (title, body, onClick) => {
  if (!('Notification' in window) || Notification.permission !== 'granted' || !document.hidden) return;
  const n = new Notification(title, { body, tag: 'partner-chat' });
  n.onclick = () => { window.focus(); onClick?.(); n.close(); };
};

const askNotificationPermission = () => {
  if ('Notification' in window && Notification.permission === 'default') Notification.requestPermission();
};

// Live connection to the partner chat namespace; calls onMessage for every new message
const usePartnerChatSocket = (token, onMessage) => {
  const handlerRef = useRef(onMessage);
  handlerRef.current = onMessage;
  useEffect(() => {
    if (!token) return undefined;
    const socket = io(`${API_BASE}/partner-chat`, { auth: { token }, transports: ['polling', 'websocket'] });
    socket.on('partner_chat_message', (msg) => handlerRef.current(msg));
    socket.on('connect_error', (err) => console.warn('Partner chat socket:', err.message));
    return () => socket.disconnect();
  }, [token]);
};

// ── Message list + composer, shared by both sides ─────────────────────────
const ChatThread = ({ messages, mySide, onSendText, onSendFile, sending, emptyText }) => {
  const [text, setText] = useState('');
  const [recState, setRecState] = useState('idle'); // idle | recording | review
  const [recSeconds, setRecSeconds] = useState(0);
  const [recBlob, setRecBlob] = useState(null);
  const [recUrl, setRecUrl] = useState('');
  const [error, setError] = useState('');
  const listRef = useRef(null);
  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const imageInputRef = useRef(null);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages]);

  const releaseMic = () => {
    clearInterval(timerRef.current);
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  };
  useEffect(() => () => { releaseMic(); if (recUrl) URL.revokeObjectURL(recUrl); }, [recUrl]);

  const startRecording = async () => {
    setError('');
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      setError('Voice recording is not supported in this browser.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mimeType = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg'].find((t) => MediaRecorder.isTypeSupported(t));
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      const chunks = [];
      recorder.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
      recorder.onstop = () => {
        releaseMic();
        const blob = new Blob(chunks, { type: recorder.mimeType || 'audio/webm' });
        setRecBlob(blob);
        setRecUrl(URL.createObjectURL(blob));
        setRecState('review');
      };
      recorderRef.current = recorder;
      recorder.start();
      setRecSeconds(0);
      setRecState('recording');
      timerRef.current = setInterval(() => {
        setRecSeconds((s) => {
          if (s + 1 >= MAX_VOICE_SECONDS && recorder.state === 'recording') recorder.stop();
          return s + 1;
        });
      }, 1000);
    } catch {
      releaseMic();
      setError('Microphone access was blocked. Please allow the microphone and try again.');
    }
  };

  const discardRecording = () => {
    if (recUrl) URL.revokeObjectURL(recUrl);
    setRecUrl('');
    setRecBlob(null);
    setRecState('idle');
  };

  const sendRecording = async () => {
    const ext = /mp4/.test(recBlob.type) ? 'm4a' : /ogg/.test(recBlob.type) ? 'ogg' : 'webm';
    const ok = await onSendFile(new File([recBlob], `voice.${ext}`, { type: recBlob.type || 'audio/webm' }));
    if (ok) discardRecording();
  };

  const submitText = async (e) => {
    e.preventDefault();
    const value = text.trim();
    if (!value) return;
    const ok = await onSendText(value);
    if (ok) setText('');
  };

  const pickImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { setError('Image must be smaller than 10 MB.'); return; }
    setError('');
    await onSendFile(file);
  };

  let lastDay = '';
  return (
    <div className="flex h-full min-h-0 flex-col">
      <div ref={listRef} className="min-h-0 flex-1 space-y-2 overflow-y-auto bg-[linear-gradient(180deg,#fffdf2,#fff)] px-3 py-3">
        {messages.length === 0 && <p className="mt-10 text-center text-sm text-gray-400">{emptyText}</p>}
        {messages.map((m) => {
          const mine = m.sender_type === mySide;
          const day = fmtDay(m.created_at);
          const showDay = day !== lastDay;
          lastDay = day;
          return (
            <React.Fragment key={m.id}>
              {showDay && <div className="py-1 text-center text-[10px] font-bold uppercase tracking-wider text-gray-400">{day}</div>}
              <div className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm shadow-sm ${mine ? 'rounded-br-md bg-yellow-400 text-black' : 'rounded-bl-md border border-gray-200 bg-white text-black'}`}>
                  {!mine && <div className="mb-0.5 text-[10px] font-black uppercase tracking-wide text-yellow-700">{m.sender_name}</div>}
                  {m.message_type === 'voice' && <audio controls preload="none" src={`${API_BASE}${m.file_url}`} className="h-9 w-56 max-w-full" />}
                  {m.message_type === 'image' && (
                    <a href={`${API_BASE}${m.file_url}`} target="_blank" rel="noreferrer">
                      <img src={`${API_BASE}${m.file_url}`} alt="Shared" className="max-h-48 rounded-lg" />
                    </a>
                  )}
                  {m.content && <div className="whitespace-pre-wrap break-words">{m.content}</div>}
                  <div className={`mt-0.5 text-right text-[10px] ${mine ? 'text-black/60' : 'text-gray-400'}`}>
                    {fmtTime(m.created_at)}{mine && (mySide === 'partner' ? (m.read_by_admin ? ' ✓✓' : ' ✓') : (m.read_by_partner ? ' ✓✓' : ' ✓'))}
                  </div>
                </div>
              </div>
            </React.Fragment>
          );
        })}
      </div>

      <div className="border-t border-yellow-100 bg-white p-2">
        {error && <p className="mb-1 px-1 text-xs font-bold text-red-600">⚠ {error}</p>}
        {recState === 'recording' && (
          <div className="flex items-center justify-between gap-2 rounded-xl bg-red-50 px-3 py-2">
            <span className="flex items-center gap-2 text-sm font-black text-red-600">
              <span className="h-2.5 w-2.5 animate-pulse rounded-full bg-red-600" />
              Recording {Math.floor(recSeconds / 60)}:{String(recSeconds % 60).padStart(2, '0')}
            </span>
            <button type="button" onClick={() => recorderRef.current?.stop()} className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-black text-white">■ Stop</button>
          </div>
        )}
        {recState === 'review' && (
          <div className="flex flex-wrap items-center gap-2 rounded-xl bg-yellow-50 px-2 py-2">
            <audio controls src={recUrl} className="h-9 min-w-0 flex-1" />
            <button type="button" onClick={discardRecording} className="rounded-lg border border-gray-300 px-2.5 py-1.5 text-xs font-bold">🗑</button>
            <button type="button" onClick={sendRecording} disabled={sending} className="rounded-lg bg-black px-3 py-1.5 text-xs font-black text-yellow-400 disabled:opacity-50">
              {sending ? 'Sending…' : 'Send ➤'}
            </button>
          </div>
        )}
        {recState === 'idle' && (
          <form onSubmit={submitText} className="flex items-end gap-1.5">
            <button type="button" onClick={() => imageInputRef.current?.click()} title="Send a photo" className="shrink-0 rounded-xl px-2 py-2 text-lg hover:bg-yellow-50">📎</button>
            <input ref={imageInputRef} type="file" accept="image/*" className="hidden" onChange={pickImage} />
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) submitText(e); }}
              rows={1}
              maxLength={4000}
              placeholder="Type a message…"
              className="max-h-28 min-h-[40px] flex-1 resize-none rounded-xl border border-yellow-200 bg-yellow-50/60 px-3 py-2 text-sm outline-none focus:border-yellow-400 focus:bg-white"
            />
            {text.trim() ? (
              <button type="submit" disabled={sending} className="shrink-0 rounded-xl bg-yellow-400 px-3 py-2 text-sm font-black text-black hover:bg-yellow-300 disabled:opacity-50">➤</button>
            ) : (
              <button type="button" onClick={startRecording} title="Record a voice message" className="shrink-0 rounded-xl bg-black px-3 py-2 text-sm text-yellow-400 hover:bg-gray-900">🎤</button>
            )}
          </form>
        )}
      </div>
    </div>
  );
};

// Pop-up shown when a message arrives and the chat is closed
const ChatToast = ({ toast, onOpen, onClose }) => (
  <div className="partner-chat-toast fixed bottom-24 right-4 z-[80] w-[300px] max-w-[calc(100vw-2rem)] cursor-pointer overflow-hidden rounded-2xl border-2 border-yellow-400 bg-white shadow-2xl" onClick={onOpen} role="alert">
    <div className="flex items-start gap-3 p-3">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-yellow-400 text-lg">💬</div>
      <div className="min-w-0 flex-1">
        <div className="text-xs font-black uppercase tracking-wide text-yellow-700">New message</div>
        <div className="truncate text-sm font-black text-black">{toast.title}</div>
        <div className="truncate text-sm text-gray-600">{toast.body}</div>
      </div>
      <button type="button" onClick={(e) => { e.stopPropagation(); onClose(); }} className="text-gray-400 hover:text-black" aria-label="Dismiss">✕</button>
    </div>
    <div className="bg-yellow-50 px-3 py-1.5 text-center text-xs font-bold text-yellow-800">Tap to open chat</div>
  </div>
);

const FloatingButton = ({ onClick, unread, label }) => (
  <button
    type="button"
    onClick={onClick}
    className="fixed bottom-5 right-5 z-[70] flex items-center gap-2 rounded-full bg-black px-4 py-3 text-sm font-black text-yellow-400 shadow-[0_10px_30px_rgba(0,0,0,0.35)] transition hover:scale-105"
    aria-label={label}
  >
    <span className="text-lg">💬</span>
    <span className="hidden sm:inline">{label}</span>
    {unread > 0 && (
      <span className="absolute -right-1 -top-1 flex h-6 min-w-[24px] animate-bounce items-center justify-center rounded-full bg-red-600 px-1.5 text-xs font-black text-white">{unread}</span>
    )}
  </button>
);

const useSender = (token) => {
  const [sending, setSending] = useState(false);
  const send = useCallback(async (url, payload) => {
    setSending(true);
    try {
      const body = new FormData();
      if (payload.text) body.append('content', payload.text);
      if (payload.file) body.append('file', payload.file);
      await axios.post(url, body, { headers: { Authorization: `Bearer ${token}` } });
      return true;
    } catch (err) {
      alert(err?.response?.data?.error || 'Message could not be sent. Please try again.');
      return false;
    } finally {
      setSending(false);
    }
  }, [token]);
  return [send, sending];
};

// ── Partner side ───────────────────────────────────────────────────────────
const PartnerChatWidget = () => {
  const token = localStorage.getItem('token');
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [unread, setUnread] = useState(0);
  const [toast, setToast] = useState(null);
  const [send, sending] = useSender(token);
  const openRef = useRef(open);
  openRef.current = open;
  const headers = { Authorization: `Bearer ${token}` };

  const markRead = () => axios.post(`${API_BASE}/api/partner/chat/read`, {}, { headers }).catch(() => {});

  useEffect(() => {
    if (!token) return;
    axios.get(`${API_BASE}/api/partner/chat/unread`, { headers }).then((r) => setUnread(r.data?.unread || 0)).catch(() => {});
  }, [token]); // eslint-disable-line

  useEffect(() => {
    if (!open) return;
    askNotificationPermission();
    axios.get(`${API_BASE}/api/partner/chat`, { headers }).then((r) => setMessages(r.data || [])).catch(() => {});
    markRead();
    setUnread(0);
    setToast(null);
  }, [open]); // eslint-disable-line

  usePartnerChatSocket(token, (msg) => {
    setMessages((prev) => (prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]));
    if (msg.sender_type !== 'admin') return;
    if (openRef.current && !document.hidden) {
      markRead();
      return;
    }
    setUnread((n) => n + 1);
    playChatChime();
    setToast({ title: 'Dani Job Club', body: previewText(msg) });
    showBrowserNotification('Dani Job Club', previewText(msg), () => setOpen(true));
  });

  if (!token) return null;
  return (
    <>
      {!open && <FloatingButton onClick={() => setOpen(true)} unread={unread} label="Chat with Dani Job Club" />}
      {toast && !open && <ChatToast toast={toast} onOpen={() => setOpen(true)} onClose={() => setToast(null)} />}
      {open && (
        <div className="partner-chat-panel fixed bottom-0 right-0 z-[75] flex h-[80vh] w-full flex-col overflow-hidden border-2 border-yellow-400 bg-white shadow-2xl sm:bottom-5 sm:right-5 sm:h-[560px] sm:w-[380px] sm:rounded-3xl">
          <div className="flex items-center justify-between bg-black px-4 py-3">
            <div>
              <div className="text-sm font-black text-yellow-400">Dani Job Club</div>
              <div className="text-[11px] text-gray-300">Chat with our team · text or voice</div>
            </div>
            <button type="button" onClick={() => setOpen(false)} className="rounded-full px-2 text-xl text-white hover:text-yellow-400" aria-label="Close chat">✕</button>
          </div>
          <div className="min-h-0 flex-1">
            <ChatThread
              messages={messages}
              mySide="partner"
              sending={sending}
              emptyText="Send us a message: questions, updates or anything about your members."
              onSendText={(text) => send(`${API_BASE}/api/partner/chat`, { text })}
              onSendFile={(file) => send(`${API_BASE}/api/partner/chat`, { file })}
            />
          </div>
        </div>
      )}
    </>
  );
};

// ── Admin side ─────────────────────────────────────────────────────────────
export const AdminPartnerChat = () => {
  const token = localStorage.getItem('adminToken');
  const [open, setOpen] = useState(false);
  const [threads, setThreads] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [search, setSearch] = useState('');
  const [toast, setToast] = useState(null);
  const [send, sending] = useSender(token);
  const stateRef = useRef({ open, activeId });
  stateRef.current = { open, activeId };
  const headers = { Authorization: `Bearer ${token}` };

  const loadThreads = useCallback(() => {
    if (!token) return;
    axios.get(`${API_BASE}/api/admin/partner-chats`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setThreads(r.data || []))
      .catch(() => {});
  }, [token]);

  useEffect(() => { loadThreads(); }, [loadThreads]);

  const markRead = (partnerId) => axios.post(`${API_BASE}/api/admin/partner-chats/${partnerId}/read`, {}, { headers }).catch(() => {});

  const openThread = (partnerId) => {
    setOpen(true);
    setActiveId(partnerId);
    setMessages([]);
    setToast(null);
    askNotificationPermission();
    axios.get(`${API_BASE}/api/admin/partner-chats/${partnerId}`, { headers }).then((r) => setMessages(r.data || [])).catch(() => setMessages([]));
    markRead(partnerId);
    setThreads((prev) => prev.map((t) => (t.partnership_submission_id === partnerId ? { ...t, unread: 0 } : t)));
  };

  usePartnerChatSocket(token, (msg) => {
    const { open: isOpen, activeId: current } = stateRef.current;
    const viewing = isOpen && current === msg.partnership_submission_id && !document.hidden;
    if (current === msg.partnership_submission_id) {
      setMessages((prev) => (prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]));
    }
    setThreads((prev) => {
      const exists = prev.some((t) => t.partnership_submission_id === msg.partnership_submission_id);
      if (!exists) { loadThreads(); return prev; }
      const updated = prev.map((t) => (t.partnership_submission_id === msg.partnership_submission_id
        ? {
          ...t,
          last_content: msg.content,
          last_message_type: msg.message_type,
          last_sender_type: msg.sender_type,
          last_created_at: msg.created_at,
          unread: msg.sender_type === 'partner' && !viewing ? (t.unread || 0) + 1 : t.unread
        }
        : t));
      // most recent conversation first
      return [...updated].sort((a, b) => (b.last_created_at || '').localeCompare(a.last_created_at || ''));
    });
    if (msg.sender_type !== 'partner') return;
    if (viewing) {
      markRead(msg.partnership_submission_id);
      return;
    }
    playChatChime();
    setToast({ title: msg.sender_name || 'Partner', body: previewText(msg), partnerId: msg.partnership_submission_id });
    showBrowserNotification(`Partner: ${msg.sender_name || 'message'}`, previewText(msg), () => openThread(msg.partnership_submission_id));
  });

  if (!token) return null;
  const totalUnread = threads.reduce((sum, t) => sum + (t.unread || 0), 0);
  const activeThread = threads.find((t) => t.partnership_submission_id === activeId);
  const q = search.trim().toLowerCase();
  const visibleThreads = threads.filter((t) => !q || [t.name_of_organisation, t.owner_of_organisation, t.partner_user_id]
    .some((v) => String(v || '').toLowerCase().includes(q)));

  return (
    <>
      {!open && <FloatingButton onClick={() => { setOpen(true); loadThreads(); }} unread={totalUnread} label="Partner Chats" />}
      {toast && !(open && activeId === toast.partnerId) && (
        <ChatToast toast={toast} onOpen={() => openThread(toast.partnerId)} onClose={() => setToast(null)} />
      )}
      {open && (
        <div className="partner-chat-panel fixed bottom-0 right-0 z-[75] flex h-[85vh] w-full overflow-hidden border-2 border-yellow-400 bg-white shadow-2xl sm:bottom-5 sm:right-5 sm:h-[600px] sm:w-[720px] sm:max-w-[calc(100vw-2.5rem)] sm:rounded-3xl">
          {/* Thread list (hidden on phones while a conversation is open) */}
          <div className={`${activeId ? 'hidden sm:flex' : 'flex'} w-full flex-col border-r border-yellow-100 sm:w-[260px]`}>
            <div className="flex items-center justify-between bg-black px-4 py-3">
              <div className="text-sm font-black text-yellow-400">Partner Chats {totalUnread > 0 && <span className="ml-1 rounded-full bg-red-600 px-1.5 text-xs text-white">{totalUnread}</span>}</div>
              <button type="button" onClick={() => setOpen(false)} className="px-2 text-xl text-white hover:text-yellow-400 sm:hidden" aria-label="Close chats">✕</button>
            </div>
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="🔍 Search partner" className="m-2 rounded-xl border border-yellow-200 bg-yellow-50/60 px-3 py-2 text-sm outline-none focus:border-yellow-400" />
            <div className="min-h-0 flex-1 overflow-y-auto">
              {visibleThreads.length === 0 && <p className="p-4 text-center text-xs text-gray-400">No partners with a login yet.</p>}
              {visibleThreads.map((t) => (
                <button
                  key={t.partnership_submission_id}
                  type="button"
                  onClick={() => openThread(t.partnership_submission_id)}
                  className={`flex w-full items-start gap-2 border-b border-gray-100 px-3 py-2.5 text-left hover:bg-yellow-50 ${activeId === t.partnership_submission_id ? 'bg-yellow-100' : ''}`}
                >
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-yellow-400 text-sm font-black">
                    {(t.name_of_organisation || '?').slice(0, 1).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-1">
                      <span className={`truncate text-sm ${t.unread ? 'font-black' : 'font-bold'} text-black`}>{t.name_of_organisation}</span>
                      <span className="shrink-0 text-[10px] text-gray-400">{t.last_created_at ? fmtTime(t.last_created_at) : ''}</span>
                    </div>
                    <div className="flex items-center justify-between gap-1">
                      <span className={`truncate text-xs ${t.unread ? 'font-bold text-black' : 'text-gray-500'}`}>
                        {t.last_created_at ? `${t.last_sender_type === 'admin' ? 'You: ' : ''}${previewText({ message_type: t.last_message_type, content: t.last_content })}` : 'No messages yet'}
                      </span>
                      {t.unread > 0 && <span className="shrink-0 rounded-full bg-red-600 px-1.5 text-[10px] font-black text-white">{t.unread}</span>}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Conversation */}
          <div className={`${activeId ? 'flex' : 'hidden sm:flex'} min-w-0 flex-1 flex-col`}>
            <div className="flex items-center justify-between gap-2 bg-black px-4 py-3">
              <div className="flex min-w-0 items-center gap-2">
                {activeId && <button type="button" onClick={() => setActiveId(null)} className="text-white sm:hidden" aria-label="Back">←</button>}
                <div className="min-w-0">
                  <div className="truncate text-sm font-black text-yellow-400">{activeThread?.name_of_organisation || 'Select a partner'}</div>
                  {activeThread && <div className="truncate text-[11px] text-gray-300">{activeThread.owner_of_organisation} · Login: {activeThread.partner_user_id}</div>}
                </div>
              </div>
              <button type="button" onClick={() => setOpen(false)} className="px-2 text-xl text-white hover:text-yellow-400" aria-label="Close chats">✕</button>
            </div>
            <div className="min-h-0 flex-1">
              {activeId ? (
                <ChatThread
                  messages={messages}
                  mySide="admin"
                  sending={sending}
                  emptyText="No messages yet. Say hello to this partner."
                  onSendText={(text) => send(`${API_BASE}/api/admin/partner-chats/${activeId}`, { text })}
                  onSendFile={(file) => send(`${API_BASE}/api/admin/partner-chats/${activeId}`, { file })}
                />
              ) : (
                <div className="flex h-full items-center justify-center p-6 text-center text-sm text-gray-400">Choose a partner on the left to read and reply.</div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PartnerChatWidget;
